import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import React, { useState } from 'react';
import moment from 'moment-timezone';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isDealProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import { timestampToDate } from '../../util/dates';
import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;
import { configForListing, userDisplayNameAsString } from '../../util/data';
import useCategoryConfigMap from '../../hooks/useCategoryConfigMap';
import EmergencyServiceCta from '../EmergencyServiceCta/EmergencyServiceCta';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  DEAL_LISTING_TYPE,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_NIGHT,
  LISTING_STATE_CLOSED,
  LISTING_UNIT_TYPES,
  SERVICE_LISTING_TYPE,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  STOCK_MULTIPLE_ITEMS,
  propTypes,
} from '../../util/types';
import LineItemBasePriceMaybe from '../OrderBreakdown/LineItemBasePriceMaybe';
import LineItemUnknownItemsMaybe from '../OrderBreakdown/LineItemUnknownItemsMaybe';
import { createSlug, parse, stringify } from '../../util/urlHelpers';

import { BookingCreditCardIcon, BookingMapPinIcon } from './BookingPageIcons';
import nowLogo from '../../assets/images/now-logo.png';
import {
  Button,
  CurrencyAmount,
  H1,
  ModalInMobile,
  NamedLink,
  PrimaryButton,
} from '../../components';
// Imported directly rather than through components/index.js because the named
// KNOWN_MODELS export isn't re-exported there.
import TransactionModelBadge, {
  KNOWN_MODELS,
} from '../TransactionModelBadge/TransactionModelBadge';
import AddToCartButton from '../AddToCartButton/AddToCartButton';

import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { POST_AUTH_ACTIONS, savePostAuthIntent } from '../../util/postAuthIntent';
import css from './OrderPanel.module.css';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const NegotiationInquiryForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationInquiryForm" */ './NegotiationInquiryForm/NegotiationInquiryForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

// category_config_versions.payment_model. The column and the admin type union
// also allow 'milestone' and 'final_invoice', but the admin's Configure dialog
// offers only these two for every transaction model (ConfigureCategoryDialog
// PAYMENT_BY_MODEL), so those two can't currently be set. Deliberately not
// listed here: if either is enabled admin-side later, the badge row hides
// itself rather than shipping an untranslated enum to customers.
export const KNOWN_PAYMENT_MODELS = ['full_payment', 'customer_split'];

const priceData = (price, currency) => {
  if (price && price.currency === currency) {
    return { price, priceTitle: 'SAR' };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency);
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <CurrencyAmount amount={price} symbolSize={18} />
      </p>
      <div className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    onSubmitInquiry,
    sendInquiryInProgress,
    sendInquiryError,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    routeConfiguration,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    onFetchStaffListings,
    config,
    staffListings,
    fetchStaffListingsInProgress,
    fetchStaffListingsError,
    cartProps,
    isDealItem,
    dealItemsListings = [],
    dealServicesData = [],
    setDealServicesData,
    setIsBookingModalOpen,
    dealItemMonthlyTimeSlots,
    onFetchDealItemTimeSlots,
    isDealValid,
    isDealStarted,
    currentUser,
    isBookingLink,
    merchantDetails,
    callSetInitialValues,
    onInitializeCardPaymentData,
    showListingSummary = false,
    bookingPageLayout = false,
    hideEmergencyCta = false,
    negotiatedOfferAccepted = false,
    negotiatedDurationHours,
  } = props;

  const isDealItemClosed = dealItemsListings.reduce((acc, item) => {
    if (item?.attributes?.state !== 'published') {
      acc = true;
    }
    return acc;
  }, false);

  const listingTitle = listing?.attributes?.title || '';
  const listingImage = listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url || null;

  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '' } = publicData || {};
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  // Same render-time join the listing cards use: resolved from the category's
  // currently active config rather than anything stored on the listing, so an
  // admin edit lands on the next load with no listing rewrite. Null for
  // legacy-taxonomy listings — the whole row then hides.
  const categoryConfigMap = useCategoryConfigMap();
  const listingConfig = configForListing(categoryConfigMap, publicData);
  // Gated the same way TransactionModelBadge gates its own set: a payment model
  // added admin-side that has no translation yet must not leak a raw enum
  // ("milestone") or render an empty pill.
  const rawPaymentModel = listingConfig?.paymentModel;
  const paymentModel = KNOWN_PAYMENT_MODELS.includes(rawPaymentModel) ? rawPaymentModel : null;
  // Mirrors CheckoutPageWithPayment.js's isCustomerSplitEligible derivation.
  // Rendered outside the showAsideSummary-gated block below (which only
  // renders on the bookingPageLayout/showListingSummary variants of this
  // component) so the notice reaches the customer on every OrderPanel
  // rendering, including the plain ListingPageCoverPhoto layout.
  const isCustomerSplitEligible = !!listingConfig?.customerSplitEnabled;
  // Both halves can be filtered out independently, so ask whether anything
  // survives before drawing the row's own border and padding.
  const showConfigRow =
    KNOWN_MODELS.includes(listingConfig?.transactionModel) || !!paymentModel;

  // Negotiated listings have no fixed price to book against -- the customer
  // just describes the job and the merchant quotes it afterwards (see the
  // negotiation sub-graph in transactionProcessBooking.js), so this skips
  // the normal date/time picker in favor of a plain description field.
  const isNegotiatedModel = listingConfig?.transactionModel === 'negotiated';

  // Once an offer is accepted, the booking's duration is whatever the merchant
  // fixed on the agreed quote -- not something the customer can pick, so this
  // reuses the same "fixed duration, no stepper" rendering FieldDateAndTimeInput
  // already has for Fixed-type listings (minimumBookingHours == null + a
  // serviceDuration in minutes) instead of the listing's own, unrelated
  // publicData. Falls back to the listing's own publicData (today's stepper
  // behavior) for quotes sent before this field existed.
  const negotiatedBookingPublicData =
    isNegotiatedModel && negotiatedOfferAccepted && Number(negotiatedDurationHours) > 0
      ? { ...publicData, serviceDuration: Number(negotiatedDurationHours) * 60, minimumBookingHours: null }
      : publicData;

  const price = listing?.attributes?.price;

  // `price` is the listing's static per-unit rate (e.g. per hour). Once a
  // booking is selected, lineItems reflect the actual duration/quantity, so
  // the summary total should track those instead of always showing the flat
  // unit price (which never changed when the customer picked more hours).
  const customerFacingLineItems =
    Array.isArray(lineItems) && lineItems.length > 0
      ? lineItems.filter(li => !li.reversal && li.includeFor?.includes('customer'))
      : [];
  const unitLineItems = customerFacingLineItems.filter(li => LISTING_UNIT_TYPES.includes(li.code));
  const customerLineItemsTotal =
    customerFacingLineItems.length > 0
      ? customerFacingLineItems.reduce((sum, li) => sum + (li.lineTotal?.amount || 0), 0)
      : null;
  const summaryPrice =
    customerLineItemsTotal != null && price
      ? new Money(customerLineItemsTotal, price.currency)
      : price;

  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !isNegotiatedModel && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency =
    isPaymentProcess && !isNegotiatedModel && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;


  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  // A negotiated listing has no fixed price to book a slot against until an
  // offer has actually been accepted -- before that, only the description
  // form (below) makes sense. Once accepted, the price is settled (agreed
  // amount lives in the last quote/counter-offer, not the listing), so the
  // customer picks a date/time exactly like any other hourly booking.
  const showBookingTimeForm =
    shouldHaveBookingTime && !isClosed && timeZone && (!isNegotiatedModel || negotiatedOfferAccepted);
  // Guarded on onSubmitInquiry being wired in: TransactionPage also renders an
  // OrderPanel (to let a customer re-book from an existing inquiry) without
  // that prop, and this form must not attempt to fire a brand new inquiry
  // from there.
  const showNegotiationForm =
    isBooking && isNegotiatedModel && !negotiatedOfferAccepted && !isClosed && !!onSubmitInquiry;

  const dealItemType =
    dealItemsListings?.length && dealItemsListings[0]?.attributes?.publicData?.listingType;
  const isServiceDeal =
    listingType === DEAL_LISTING_TYPE && dealItemType === SERVICE_LISTING_TYPE;
  const dealServiceItem = isServiceDeal ? dealItemsListings[0] : null;
  const serviceTimeZone = dealServiceItem?.attributes?.availabilityPlan?.timezone;
  // A deal's own category is a day-unit placeholder with no real
  // transactionModel — the price/label semantics come from the linked
  // SERVICE's category instead (matches server/api/initiate-privileged.js).
  const dealServiceConfig = isServiceDeal
    ? configForListing(categoryConfigMap, dealServiceItem?.attributes?.publicData)
    : null;
  const isFixedModel = (isServiceDeal ? dealServiceConfig : listingConfig)?.transactionModel === 'fixed';

  const shouldHaveBookingDates =
    isDealProcess && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm =
    shouldHaveBookingDates && !isClosed && timeZone && !isServiceDeal;
  const showServiceDealBookingForm =
    isServiceDeal && dealServiceItem && !isClosed && serviceTimeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = isPurchase && typeof currentStock === 'number';

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const [bookingFormValues, setBookingFormValues] = useState(null);

  const summaryDateValue = (() => {
    const date = bookingFormValues?.bookingStartDate?.date;
    if (!date) {
      return '—';
    }
    return intl.formatDate(date, { weekday: 'short', month: 'short', day: 'numeric' });
  })();

  // For a service deal, the slot picker (BookingTimeForm below) is always
  // driven by the linked service's timezone (serviceTimeZone), never the
  // deal listing's own — so the summary must match that, not the other way
  // around. The deal listing can easily carry its own unrelated
  // availabilityPlan.timezone (defaulted to whichever browser last edited
  // it, via getDefaultTimeZoneOnBrowser in EditListingPage.duck.js) even
  // though it plays no part in choosing the slot, which used to make this
  // prefer that stray value over the service's real one whenever it existed.
  const effectiveTimeZone = isServiceDeal ? serviceTimeZone || timeZone : timeZone;

  const summaryTimeValue = (() => {
    const startTime = bookingFormValues?.bookingStartTime;
    if (!startTime || Number.isNaN(Number(startTime))) {
      return '—';
    }
    const toClockTime = timestamp =>
      effectiveTimeZone
        ? moment.tz(Number(timestamp), effectiveTimeZone)
        : moment(Number(timestamp));
    const startClock = toClockTime(startTime).format('HH:mm');

    const endTime = bookingFormValues?.bookingEndTime;
    if (!endTime || Number.isNaN(Number(endTime))) {
      return startClock;
    }
    return `${startClock} - ${toClockTime(endTime).format('HH:mm')}`;
  })();

  const summaryStaffValue = (() => {
    const staffId = bookingFormValues?.selectedStaff;
    if (!staffId) {
      return '—';
    }
    const staff = staffListings?.find(s => s?.id?.uuid === staffId);
    return staff?.attributes?.title || '—';
  })();

  const classes = classNames(
    rootClassName || css.root,
    className,
    bookingPageLayout && css.bookingPageSplit,
    arabicLanguage ? css.arabicLanguage : null
  );
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const shouldDisableDeal =
    listingType === DEAL_LISTING_TYPE
      ? !isDealValid || !isDealStarted || isDealItemClosed || !dealItemsListings?.length
      : false;

  const usesBookingTimeSelection = showBookingTimeForm || showServiceDealBookingForm;
  const isBookingTimeSelectionComplete = (() => {
    if (!usesBookingTimeSelection) {
      return true;
    }
    const startTime = bookingFormValues?.bookingStartTime;
    const endTime = bookingFormValues?.bookingEndTime;
    const selectedStaff = bookingFormValues?.selectedStaff;
    const startDate = startTime ? timestampToDate(startTime) : null;
    return !!(selectedStaff && startDate && startTime && endTime);
  })();

  const isAsideCheckoutDisabled =
    shouldDisableDeal || (usesBookingTimeSelection && !isBookingTimeSelectionComplete);

  const addToCartButton = ({ extraParams, shouldDisable }) => (
    <AddToCartButton
      showProductOrderForm={true}
      isBooking={false}
      buttonLabel={<FormattedMessage id="AddToCartButton.label" />}
      cartLabel={<FormattedMessage id="AddToCartButton.cartLabel" />}
      disabled={shouldDisable || shouldDisableDeal}
      extraParams={{
        ...extraParams,
        title: listingTitle,
        image: listingImage,
      }}
      {...cartProps}
    />
  );

  const getUserCheckout = checkoutData => {
    if (isBookingLink) {
      typeof window !== 'undefined' &&
        window.localStorage.setItem('checkoutData', JSON.stringify(checkoutData));
      history.push(
        createResourceLocatorString(
          'SignupForUserTypePage',
          routeConfiguration,
          { userType: 'customer' },
          { bookingLink: true }
        )
      );
      return;
    }

    const listing = checkoutData?.listing || {};
    const listingId = listing?.id?.uuid;
    const { displayName: authorName } = listing?.author?.attributes?.profile || {};

    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routeConfiguration);
    callSetInitialValues(setInitialValues, checkoutData, true);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    const checkoutPath = createResourceLocatorString('CheckoutPage', routeConfiguration, {
      id: listingId,
      slug: createSlug(authorName),
    });

    savePostAuthIntent({
      returnPath: checkoutPath,
      action: POST_AUTH_ACTIONS.CHECKOUT,
      listingId,
      createdAt: Date.now(),
    });

    history.push(checkoutPath);
  };

  const locationRaw = merchantDetails?.location || publicData?.location;
  const listingLocation =
    locationRaw?.selectedPlace?.address ||
    locationRaw?.address ||
    (typeof locationRaw === 'string' ? locationRaw : '');

  const bookingFormId =
    showServiceDealBookingForm || showBookingTimeForm
      ? 'OrderPanelBookingTimeForm'
      : showNegotiationForm
    ? 'OrderPanelNegotiationForm'
    : showBookingDatesForm
    ? 'OrderPanelBookingDatesForm'
    : showProductOrderForm
    ? 'OrderPanelProductOrderForm'
    : 'OrderPanelInquiryForm';

  const showAsideSummary = bookingPageLayout || (showListingSummary && listingImage);

  const asideSummary =
    showAsideSummary ? (
      <div className={bookingPageLayout ? css.bookingAsideCard : css.summaryHeader}>
        <img
          className={classNames(css.summaryImage, !listingImage && css.summaryImagePlaceholder)}
          src={listingImage || nowLogo}
          alt={listingTitle}
        />
        <h2 className={css.summaryTitle}>{listingTitle}</h2>
        {listingLocation ? (
          <p className={css.summaryLocation}>
            <BookingMapPinIcon className={css.summaryLocationIcon} />
            <span>{listingLocation}</span>
          </p>
        ) : null}
        {/* How this category is transacted and paid. The badge is the same
            component the search cards use, so the pill a customer clicked in
            search is the pill they land on here. Renders nothing when the
            category has no active config. */}
        {showConfigRow ? (
          <div className={css.configRow}>
            <TransactionModelBadge config={listingConfig} />
            {paymentModel ? (
              <span className={css.configPayment}>
                <FormattedMessage id="OrderPanel.paymentModel" values={{ paymentModel }} />
              </span>
            ) : null}
          </div>
        ) : null}
        {bookingPageLayout && isNegotiatedModel && !negotiatedOfferAccepted ? (
          <div className={css.negotiatedSummaryBanner}>
            <span className={css.negotiatedSummaryBannerIcon} aria-hidden="true">
              💬
            </span>
            <span>
              <FormattedMessage id="OrderPanel.negotiatedSummaryInfo" />
            </span>
          </div>
        ) : null}
        {bookingPageLayout && (!isNegotiatedModel || negotiatedOfferAccepted) ? (
          <dl className={css.summaryDetailsList}>
            <div className={css.summaryDetailRow}>
              <dt className={css.summaryDetailLabel}>
                <FormattedMessage id="OrderPanel.summaryDate" />
              </dt>
              <dd className={css.summaryDetailValue}>{summaryDateValue}</dd>
            </div>
            <div className={css.summaryDetailRow}>
              <dt className={css.summaryDetailLabel}>
                <FormattedMessage id="OrderPanel.summaryTime" />
              </dt>
              <dd className={css.summaryDetailValue}>{summaryTimeValue}</dd>
            </div>
            <div className={css.summaryDetailRow}>
              <dt className={css.summaryDetailLabel}>
                <FormattedMessage id="OrderPanel.summaryStaff" />
              </dt>
              <dd className={css.summaryDetailValue}>{summaryStaffValue}</dd>
            </div>
          </dl>
        ) : null}
        {unitLineItems.length > 0 ? (
          <div className={css.summaryBreakdown}>
            {unitLineItems.map((uli, idx) => (
              <LineItemBasePriceMaybe
                lineItems={[uli]}
                key={idx}
                code={uli.code}
                intl={intl}
                isFixedModel={isFixedModel}
                isNegotiatedModel={isNegotiatedModel}
              />
            ))}
            <LineItemUnknownItemsMaybe
              lineItems={customerFacingLineItems}
              isProvider={false}
              intl={intl}
            />
          </div>
        ) : null}
        {price && (!isNegotiatedModel || negotiatedOfferAccepted) ? (
          <div className={css.summaryTotalRow}>
            <span className={css.summaryTotalLabel}>
              <FormattedMessage id="OrderPanel.totalLabel" />
            </span>
            <span className={css.summaryTotalValue}>
              <CurrencyAmount amount={summaryPrice} symbolSize={22} />
            </span>
          </div>
        ) : null}
        {bookingPageLayout &&
        (showBookingTimeForm ||
          showServiceDealBookingForm ||
          showBookingDatesForm ||
          showNegotiationForm ||
          showProductOrderForm) ? (
          <button
            type="submit"
            form={bookingFormId}
            className={css.bookingAsideCta}
            disabled={isAsideCheckoutDisabled}
          >
            <BookingCreditCardIcon />
            {showNegotiationForm ? (
              <FormattedMessage id="OrderPanel.getQuoteButton" />
            ) : isBooking ? (
              <FormattedMessage id="OrderPanel.completePayment" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : (
              <FormattedMessage id="OrderPanel.completeBooking" />
            )}
          </button>
        ) : null}
      </div>
    ) : null;

  const formPanel = (
    <>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        className={classNames(arabicLanguage ? css.arabicLanguage : null, css.modalMain)}
      >
        {/* <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div> */}

        {showBookingTimeForm && !bookingPageLayout ? (
          <div className={css.selectTimeText}>
            <FormattedMessage id="OrderPanel.selectTime" />
          </div>
        ) : null}

        <div
          className={classNames(css.panelContents, bookingPageLayout && css.panelContentsBooking)}
        >
          {hideEmergencyCta ? null : (
            <EmergencyServiceCta
              listing={listing}
              currentUser={currentUser}
              isAuthenticated={!!currentUser}
            />
          )}
          {isCustomerSplitEligible ? (
            <div className={classNames(css.negotiatedSummaryBanner, css.customerSplitBanner)}>
              <span>
                <FormattedMessage id="OrderPanel.customerSplitNotice" />
              </span>
            </div>
          ) : null}
          {listingType === DEAL_LISTING_TYPE && !isDealStarted ? (
            <p className={css.noMoreText}>
              <FormattedMessage id="OrderPanel.hasNotStarted" />
            </p>
          ) : listingType === DEAL_LISTING_TYPE && !isDealValid ? (
            <p className={css.noMoreText}>
              <FormattedMessage id="OrderPanel.noMoreActive" />
            </p>
          ) : null}

          {showPriceMissing ? (
            <PriceMissing />
          ) : showInvalidCurrency ? (
            <InvalidCurrency />
          ) : showBookingTimeForm ? (
            <BookingTimeForm
              className={css.bookingForm}
              formId="OrderPanelBookingTimeForm"
              bookingPageLayout={bookingPageLayout}
              lineItemUnitType={lineItemUnitType}
              onSubmit={onSubmit}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              timeZone={timeZone}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onFetchStaffListings={onFetchStaffListings}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              publicData={negotiatedBookingPublicData}
              config={config}
              staffListings={staffListings}
              fetchStaffListingsError={fetchStaffListingsError}
              fetchStaffListingsInProgress={fetchStaffListingsInProgress}
              addToCartButton={addToCartButton}
              isDealItem={isDealItem}
              setDealServicesData={setDealServicesData}
              setIsBookingModalOpen={setIsBookingModalOpen}
              currentUser={currentUser}
              isBookingLink={isBookingLink}
              listing={listing}
              merchantDetails={merchantDetails}
              getUserCheckout={getUserCheckout}
              history={history}
              routeConfiguration={routeConfiguration}
              onBookingFormValuesChange={bookingPageLayout ? setBookingFormValues : undefined}
            />
          ) : showServiceDealBookingForm ? (
            <BookingTimeForm
              className={css.bookingForm}
              formId="OrderPanelBookingTimeForm"
              bookingPageLayout={bookingPageLayout}
              dealMode
              dealListing={listing}
              lineItemUnitType={LINE_ITEM_HOUR}
              onSubmit={onSubmit}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              listingId={dealServiceItem.id}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={dealItemMonthlyTimeSlots}
              onFetchTimeSlots={onFetchDealItemTimeSlots}
              startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
              timeZone={serviceTimeZone}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onFetchStaffListings={onFetchStaffListings}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              publicData={dealServiceItem.attributes?.publicData}
              config={config}
              staffListings={staffListings}
              fetchStaffListingsError={fetchStaffListingsError}
              fetchStaffListingsInProgress={fetchStaffListingsInProgress}
              addToCartButton={addToCartButton}
              currentUser={currentUser}
              isBookingLink={isBookingLink}
              listing={dealServiceItem}
              merchantDetails={merchantDetails}
              getUserCheckout={getUserCheckout}
              history={history}
              routeConfiguration={routeConfiguration}
              onBookingFormValuesChange={bookingPageLayout ? setBookingFormValues : undefined}
            />
          ) : showNegotiationForm ? (
            <NegotiationInquiryForm
              className={css.bookingForm}
              formId="OrderPanelNegotiationForm"
              onSubmit={onSubmitInquiry}
              inProgress={sendInquiryInProgress}
              sendInquiryError={sendInquiryError}
            />
          ) : showBookingDatesForm ? (
            <BookingDatesForm
              className={css.bookingForm}
              formId="OrderPanelBookingDatesForm"
              bookingPageLayout={bookingPageLayout}
              lineItemUnitType={lineItemUnitType}
              onSubmit={onSubmit}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              dayCountAvailableForBooking={dayCountAvailableForBooking}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              timeZone={timeZone}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              addToCartButton={addToCartButton}
              showBreakdown={false}
              listingPublicData={publicData}
              dealItemsListings={dealItemsListings}
              dealServicesData={dealServicesData}
              currentUser={currentUser}
              isBookingLink={isBookingLink}
              listing={listing}
              merchantDetails={merchantDetails}
              shouldDisableDeal={shouldDisableDeal}
              getUserCheckout={getUserCheckout}
              history={history}
              routeConfiguration={routeConfiguration}
            />
          ) : showProductOrderForm ? (
            <ProductOrderForm
              formId="OrderPanelProductOrderForm"
              bookingPageLayout={bookingPageLayout}
              onSubmit={onSubmit}
              price={price}
              marketplaceCurrency={marketplaceCurrency}
              currentStock={currentStock}
              allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
              pickupEnabled={pickupEnabled && displayPickup}
              shippingEnabled={shippingEnabled && displayShipping}
              displayDeliveryMethod={displayPickup || displayShipping}
              listingId={listing.id}
              isOwnListing={isOwnListing}
              marketplaceName={marketplaceName}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              onContactUser={onContactUser}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              payoutDetailsWarning={payoutDetailsWarning}
              addToCartButton={addToCartButton}
              isBookingLink={isBookingLink}
              listing={listing}
              merchantDetails={merchantDetails}
              getUserCheckout={getUserCheckout}
              history={history}
              routeConfiguration={routeConfiguration}
              isClosed={isClosed}
            />
          ) : showInquiryForm ? (
            <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
          ) : !isKnownProcess ? (
            <p className={css.errorSidebar}>
              <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
            </p>
          ) : null}
        </div>
      </ModalInMobile>

      {cartProps?.count ? (
        <NamedLink className={css.cartLink} name="CartPage">
          {listingType === SERVICE_LISTING_TYPE ? (
            <FormattedMessage id="OrderPanel.completeBooking" />
          ) : (
            <FormattedMessage id="OrderPanel.completeOrder" />
          )}
        </NamedLink>
      ) : null}
    </>
  );

  if (bookingPageLayout) {
    return (
      <div className={classes}>
        <div className={css.bookingFormColumn}>{formPanel}</div>
        {asideSummary ? <aside className={css.bookingAsideColumn}>{asideSummary}</aside> : null}
      </div>
    );
  }

  return (
    <div className={classes}>
      {showListingSummary && !bookingPageLayout ? asideSummary : null}
      {formPanel}

      {/* {!isDealItem ? (
        <div className={css.openOrderForm}>
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
            showCurrencyMismatch
          />
          {isClosed ? (
            <div className={css.closedListingButton}>
              <FormattedMessage id="OrderPanel.closedListingButtonText" />
            </div>
          ) : (
            <Button
              className={css.ctaButton}
              onClick={handleSubmit(
                isOwnListing,
                isClosed,
                showInquiryForm,
                onSubmit,
                history,
                location
              )}
              disabled={isOutOfStock || shouldDisableDeal}
            >
              {isBooking ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
              ) : isOutOfStock ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
              ) : isPurchase ? (
                <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
              ) : (
                <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
              )}
            </Button>
          )}
        </div>)
        : null} */}
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  onSubmitInquiry: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  onSubmitInquiry: func,
  sendInquiryInProgress: bool,
  sendInquiryError: propTypes.error,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withRouter, injectIntl)(OrderPanel);
